import { computed, ref, unref } from "vue";
import { useHead } from "@unhead/vue";
import { useRoute } from "vue-router";

const SITE_URL = "https://kalisistemas.com.br";
const DEFAULT_IMAGE = `${SITE_URL}/dashboard.png`;
const API_BASE =
  import.meta.env.VITE_API_BASE_URL || "https://api.kalisistemas.com.br/api";

// Config de SEO carregada em runtime a partir do painel admin.
// Cache em módulo: buscada uma única vez e compartilhada por todas as páginas.
const seoConfig = ref({ enabled: true, site_url: SITE_URL, og_image_url: null, route_images: {}, route_titles: {}, route_descriptions: {} });
let seoConfigPromise = null;

// Exportada para que a build SSG possa AGUARDAR a config do admin antes de
// renderizar (congelando og_image_url/route_images no HTML — ver main.js).
// No cliente, é disparada no carregamento do módulo (abaixo) sem bloquear.
export function loadSeoConfig() {
  if (seoConfigPromise) return seoConfigPromise;
  seoConfigPromise = fetch(`${API_BASE}/public/seo-settings`)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      seoConfig.value = {
        enabled: data?.enabled !== false,
        site_url: data?.site_url || SITE_URL,
        og_image_url: data?.og_image_url || null,
        route_images: data?.route_images && typeof data.route_images === "object" ? data.route_images : {},
        route_titles: data?.route_titles && typeof data.route_titles === "object" ? data.route_titles : {},
        route_descriptions: data?.route_descriptions && typeof data.route_descriptions === "object" ? data.route_descriptions : {},
      };
    })
    .catch(() => {
      // API indisponível: mantém os defaults (SEO dinâmico ativo, imagem local)
    });
  return seoConfigPromise;
}

loadSeoConfig();

function resolveImage(image, fallback) {
  if (!image) return fallback;
  if (/^https?:\/\//.test(image)) return image;
  const base = seoConfig.value.site_url || SITE_URL;
  return `${base}${image.startsWith("/") ? "" : "/"}${image}`;
}

export function useSeo(input) {
  const route = useRoute();

  const resolved = computed(() => {
    const options = typeof input === "function" ? input() : unref(input);
    const {
      title,
      description,
      image,
      type = "website",
      schema,
      noindex = false,
    } = options || {};

    if (!seoConfig.value.enabled) {
      return {
        ...(title ? { title } : {}),
        meta: [
          { name: "description", content: description },
          ...(noindex ? [{ name: "robots", content: "noindex, follow" }] : []),
        ].filter((m) => m.content),
        link: [],
        script: [],
      };
    }

    const siteUrl = seoConfig.value.site_url || SITE_URL;
    const url = `${siteUrl}${route.path}`;
    const fallbackImage = seoConfig.value.og_image_url || DEFAULT_IMAGE;
    const routeImage = seoConfig.value.route_images?.[route.path] || null;
    const imageUrl = routeImage || resolveImage(image, fallbackImage);

    // Texto do admin sobrescreve o código (maior prioridade)
    const adminTitle = seoConfig.value.route_titles?.[route.path] || null;
    const adminDescription = seoConfig.value.route_descriptions?.[route.path] || null;
    const resolvedTitle = adminTitle || title;
    const resolvedDescription = adminDescription || description;

    const meta = [
      { name: "description", content: resolvedDescription },
      { property: "og:type", content: type },
      { property: "og:url", content: url },
      { property: "og:title", content: resolvedTitle },
      { property: "og:description", content: resolvedDescription },
      { property: "og:image", content: imageUrl },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:url", content: url },
      { name: "twitter:title", content: resolvedTitle },
      { name: "twitter:description", content: resolvedDescription },
      { name: "twitter:image", content: imageUrl },
      ...(noindex ? [{ name: "robots", content: "noindex, follow" }] : []),
    ].filter((m) => m.content);

    const link = [{ rel: "canonical", href: url }];

    const script = schema
      ? [{ type: "application/ld+json", children: JSON.stringify(schema) }]
      : [];

    return { ...(resolvedTitle ? { title: resolvedTitle } : {}), meta, link, script };
  });

  useHead(resolved);

  return resolved;
}
